import React, { FC, useCallback, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';
import { useSelector } from 'react-redux';

import BulletListIcon from '@atlaskit/icon/glyph/bullet-list';
import { SkeletonItem } from '@atlaskit/menu';
import { Flex } from '@atlaskit/primitives';
import SectionMessage, {
  SectionMessageAction,
} from '@atlaskit/section-message';

import { useIntl } from 'src/hooks/intl';
import {
  getIsMergeChecklistLoading,
  getPullRequestMergeChecks,
  getPullRequestMergeChecksError,
} from 'src/redux/pull-request/selectors';
import { getRepositoryAccessLevel } from 'src/selectors/repository-selectors';
import { ForgeMergeCheckResult } from 'src/types/custom-merge-checks';
import { HttpError } from 'src/utils/http-request-error';

import { ConnectedExpander } from '../connected-expander';

import { useMergeChecksFetchRetry, usePollingResults } from './hooks';
import { messages } from './i18n';
import { MergeChecksEmptyState } from './merge-checks-empty-state';
import { RichMergeChecksList } from './merge-checks-lists';
import { OverallStatusIcon } from './overall-status-icon';
import {
  headingSkeletonOverrideCss,
  mergeCheckItemSkeletonOverrideCss,
} from './styled';

const FORGE_MERGE_CHECKS_STATUS_COLLAPSED_LOCALSTORAGE_KEY =
  'forge.merge.checks.status.collapsed';

/* eslint @typescript-eslint/ban-types: "warn" */
export const MergeChecksCard: FC<{}> = () => {
  const { data: customMergeChecksData, error: customMergeChecksError } =
    usePollingResults();
  const customMergeCheckResults = customMergeChecksData?.results;
  const { formatMessage } = useIntl();
  const { isMergeChecksFetchRetrying, retryMergeChecksFetch } =
    useMergeChecksFetchRetry();

  const classicMergeCheckResults = useSelector(getPullRequestMergeChecks);
  const classicMergeChecksLoading = useSelector(getIsMergeChecklistLoading);
  const classicMergeChecksError = useSelector(getPullRequestMergeChecksError);
  const repositoryAccessLevel = useSelector(getRepositoryAccessLevel);

  const icon = (
    <BulletListIcon label={formatMessage(messages.checksIconLabel)} />
  );

  const resolvedCustomChecksCount =
    customMergeCheckResults?.filter(result => result.status === 'SUCCESS')
      .length ?? 0;
  const totalCustomChecksCount = customMergeCheckResults?.length ?? 0;

  const resolvedClassicChecksCount = classicMergeCheckResults.filter(
    result => result.pass
  ).length;
  const totalClassicChecksCount = classicMergeCheckResults.length;

  const totalChecksCount = totalCustomChecksCount + totalClassicChecksCount;
  const checksCountI18nValues = {
    resolved: resolvedCustomChecksCount + resolvedClassicChecksCount,
    total: totalChecksCount,
  };

  // If there are no checks, then this is essentially the number `0`, otherwise it's
  // `resolved of total`, e.g. `1 of 2`
  const checksCountI18n = formatMessage(
    messages.checksCount,
    checksCountI18nValues
  );

  // Basically the word `check` or `checks` depending on the number of checks
  const checksLabelI18n = formatMessage(
    messages.checksLabel,
    checksCountI18nValues
  );

  const checksCountLabelI18nValues = {
    checksCount: checksCountI18n,
    checksLabel: checksLabelI18n,
  };
  const checksLabel =
    totalChecksCount === 0
      ? formatMessage(messages.emptyChecks, checksCountLabelI18nValues)
      : formatMessage(messages.checksPassed, checksCountLabelI18nValues);

  const renderChecksCountHeader = useCallback(() => {
    const i18nValues = {
      checksCount: <strong>{checksCountI18n}</strong>,
      checksLabel: checksLabelI18n,
    };

    return (
      <>
        {totalChecksCount === 0 ? (
          <div data-testid="emptyChecksCountHeader">
            <FormattedMessage {...messages.emptyChecks} values={i18nValues} />
          </div>
        ) : (
          <div data-testid="checksPassedCountHeader">
            <FormattedMessage {...messages.checksPassed} values={i18nValues} />
          </div>
        )}
      </>
    );
  }, [checksCountI18n, checksLabelI18n, totalChecksCount]);

  const { preMergeCheckResults, onMergeCheckResults } = useMemo(
    () =>
      (customMergeCheckResults ?? []).reduce(
        (acc, result) => {
          if (result.checkType === 'pre-merge') {
            acc.preMergeCheckResults.push(result);
          } else if (result.checkType === 'on-merge') {
            acc.onMergeCheckResults.push(result);
          }
          return acc;
        },
        {
          preMergeCheckResults: [] as ForgeMergeCheckResult[],
          onMergeCheckResults: [] as ForgeMergeCheckResult[],
        }
      ),
    [customMergeCheckResults]
  );

  // The custom merge check results endpoint will return a 404 if the PR has not been picked up by
  // the checks service yet. In this case we just want to continue to show the
  // loading skeleton instead of an error message, since the PR will eventually
  // be picked up and we'll get valid results.
  const isCustomMergeChecksLoading =
    !customMergeCheckResults &&
    (!customMergeChecksError ||
      (customMergeChecksError as HttpError).status === 404);
  const isLoading =
    isCustomMergeChecksLoading ||
    classicMergeChecksLoading ||
    // Relying only the loading state from the custom merge checks hook has a side effect because
    // we constantly poll the results, and the loading state will be affected by each poll. So we
    // need to rely on a separate state (provided by the useMergeChecksRetry hook) which tracks the
    // retry progress.
    isMergeChecksFetchRetrying;

  const emptyResults =
    classicMergeCheckResults.length === 0 &&
    customMergeCheckResults &&
    customMergeCheckResults.length === 0;

  // must be repo admin to see empty state because the options related
  // to setting up checks is only applicable to repo admins
  const showEmptyState = emptyResults && repositoryAccessLevel === 'admin';

  const connectedExpanderProps = {
    localStorageKey: FORGE_MERGE_CHECKS_STATUS_COLLAPSED_LOCALSTORAGE_KEY,
    icon,
    label: isLoading ? (
      <SkeletonItem
        cssFn={headingSkeletonOverrideCss}
        isShimmering
        testId="MergeChecksCardLoadingHeading"
        width="100%"
      />
    ) : (
      <>{renderChecksCountHeader()}</>
    ),
    sectionAriaLabel: formatMessage(messages.sectionAriaLabel),
    headingAriaLabel: checksLabel,
    renderSecondaryIcon: () => (
      <OverallStatusIcon
        isLoading={isLoading}
        emptyResults={emptyResults}
        preMergeCheckResults={preMergeCheckResults}
        classicMergeCheckResults={classicMergeCheckResults}
      />
    ),
  };

  if (isLoading) {
    return (
      <ConnectedExpander {...connectedExpanderProps}>
        {[...Array(5).keys()].map(index => (
          <SkeletonItem
            key={index}
            cssFn={mergeCheckItemSkeletonOverrideCss}
            hasAvatar
            isShimmering
            testId="forgeMergeCheckStatusLoadingItem"
            width="100%"
          />
        ))}
      </ConnectedExpander>
    );
  }

  const errorSectionMessage =
    customMergeChecksError || classicMergeChecksError ? (
      <SectionMessage
        appearance="warning"
        testId="mergeChecksErrorSectionMessage"
        actions={[
          <SectionMessageAction
            key="tryAgain"
            testId="mergeChecksErrorTryAgainAction"
            onClick={retryMergeChecksFetch}
          >
            {formatMessage(messages.tryAgain)}
          </SectionMessageAction>,
        ]}
      >
        {formatMessage(messages.errorText)}
      </SectionMessage>
    ) : null;

  if (emptyResults && !showEmptyState) {
    return (
      <ConnectedExpander {...connectedExpanderProps}>
        {errorSectionMessage && (
          <Flex direction="column" gap="space.100">
            {errorSectionMessage}
          </Flex>
        )}
      </ConnectedExpander>
    );
  }

  if (emptyResults && showEmptyState) {
    return (
      <ConnectedExpander {...connectedExpanderProps}>
        <Flex direction="column" gap="space.100">
          {errorSectionMessage}
          <MergeChecksEmptyState />
        </Flex>
      </ConnectedExpander>
    );
  }

  return (
    <ConnectedExpander {...connectedExpanderProps}>
      <Flex direction="column" gap="space.100">
        {errorSectionMessage}
        <RichMergeChecksList
          preMergeCheckResults={preMergeCheckResults}
          onMergeCheckResults={onMergeCheckResults}
          classicMergeCheckResults={classicMergeCheckResults}
        />
      </Flex>
    </ConnectedExpander>
  );
};
